import { cn } from './cn';

// Label + value tile — the one stat component in the app.
//
// Consolidates the two StatTile implementations that existed side by side
// (src/components/StatTile.jsx and this file) plus the StatCard / SummaryStat /
// SumCard / SeasonStat / StatChip variants scattered through the views.
//
//   variant="card"  framed tile, for stat grids (default)
//   variant="bare"  label + value only, for stat rows inside another card
//   order="label-first" (default) | "value-first"
//
// The value is always mono + tabular so columns of times line up, and the label
// is a real <dt>/<dd> pair so a screen reader reads "Beste ronde, 38.12".

const TILE_TONE = {
  ice: 'border-ice-500/35 bg-ice-100',
  accent: 'border-accent-500/30 bg-accent-100',
  green: 'border-green-500/30 bg-green-100',
};

const VALUE_TONE = {
  speed: 'text-primary-600',
  hr: 'text-red-600',
  green: 'text-green-600',
  red: 'text-red-600',
  accent: 'text-accent-600',
  ice: 'text-ice-600',
  primary: 'text-primary-600',
};

// lg/xl schalen mee met de viewport: op een telefoon zijn 26–28px cijfers naast
// een label te fors en breken de tegels.
const VALUE_SIZE = {
  sm: 'text-[15px]',
  md: 'text-[19px]',
  lg: 'type-stat-lg',
  xl: 'type-stat-xl',
};

export default function StatTile({
  label,
  value,
  unit,
  sub,
  tone = 'default',
  variant = 'card',
  size,
  order = 'label-first',
  align = 'start',
  className = '',
}) {
  const isCard = variant === 'card';
  const valueSize = VALUE_SIZE[size ?? (isCard ? 'lg' : 'md')] ?? VALUE_SIZE.md;

  const labelEl = (
    <dt className="font-condensed text-[13px] font-bold uppercase tracking-[0.08em] text-ink-3">{label}</dt>
  );
  const valueEl = (
    <dd className={cn('max-w-full font-mono font-bold leading-[1.15] tabular-nums', valueSize, VALUE_TONE[tone] ?? 'text-ink')}>
      {value}
      {unit && <span className={cn('ml-[3px] text-[14.5px] font-medium', tone === 'speed' || tone === 'hr' ? 'text-inherit' : 'text-ink-3')}>{unit}</span>}
    </dd>
  );

  return (
    <dl
      className={cn(
        'flex min-w-0 flex-col gap-[2px]',
        align === 'end' ? 'items-end text-right' : align === 'center' ? 'items-center text-center' : 'items-start',
        isCard && 'rounded-lg border border-line bg-surface px-4 py-3 shadow-e1',
        isCard && (TILE_TONE[tone] ?? ''),
        className,
      )}
    >
      {order === 'value-first' ? (
        <>
          {valueEl}
          {labelEl}
        </>
      ) : (
        <>
          {labelEl}
          {valueEl}
        </>
      )}
      {sub != null && <dd className="text-[13.5px] text-ink-3">{sub}</dd>}
    </dl>
  );
}
